/**
 * src/ui/playoff-prediction-card.js — H4 migración (26-may-2026).
 *
 * Componente compartido para la UI de predicción de un partido de PLAYOFF
 * (octavos, cuartos, semifinal, final). Vive en 2 contextos del producto:
 *
 *   - Home del grupo (grupo.js · DOM regular) → renderPlayoffCardHome().
 *     Formato banderas grandes + inputs entre medio + bloque empate +
 *     botón Guardar / Editar predicción. Validado en /dev-sandbox sec 2.
 *
 *   - Prode Playoffs (prode.js · Shadow DOM) → renderPlayoffCardProde().
 *     Formato fila compacta clonada de prode.js renderMatchCard.
 *     Validado en /dev-sandbox sec 3.
 *
 * Persistencia: ambos formatos delegan vía el callback `onSave(payload)`
 * → consumer llama al RPC `guardar_prediccion` con
 *   prediction_type='playoff_match'. El componente NO toca Supabase.
 *
 * CSS: exportado como constante PLAYOFF_CARD_CSS. El consumer lo inyecta
 * en el contexto adecuado (DOM regular: <style> tag global una vez;
 * Shadow DOM de Prode: concatenado a PRODE_CSS antes del shadow.innerHTML).
 *
 * Esta extracción cierra parcialmente:
 *   - DT-DP (card grupo del home duplicada en sandbox) → la PARTE PLAYOFF
 *     queda unificada; la parte de fase de grupos sigue en grupo.js.
 *   - DT-DQ (card prode duplicada en sandbox) → idem para playoff.
 */

// Subestado + minuto en vivo (mismo helper/campos que usan grupos y PRODE).
import { lineasEstadoVivo } from '../utils/estado-partido.js';

// ─── Validación canónica de la predicción ──────────────────────────────────
// Espejo de prode.js playoffPredictionReady. Re-exportada por si algún test
// la necesita; no se importa desde otro módulo de producción.
export const isPlayoffPredictionReady = (pred) => {
    if (!pred) return false;
    if (pred.goalsHome === '' || pred.goalsAway === '') return false;
    if (pred.goalsHome === undefined || pred.goalsAway === undefined) return false;
    const home = Number(pred.goalsHome);
    const away = Number(pred.goalsAway);
    if (!Number.isFinite(home) || !Number.isFinite(away)) return false;
    if (home === away && !pred.clasificado_codigo) return false;
    return true;
};

// Helpers de codificación (espejo de prode.js seedPredictions/persistPrediction).
export const decodePrediccionPlayoff = (row, partido) => {
    if (!row) return null;
    return {
        goalsHome: row.score_local ?? '',
        goalsAway: row.score_visitante ?? '',
        clasificado_codigo: row.clasificado_codigo || null,
        puntos: row.puntos_obtenidos ?? 0,
    };
};

const labelPrediccion = (pred, partido) => {
    const h = Number(pred.goalsHome);
    const a = Number(pred.goalsAway);
    if (!Number.isFinite(h) || !Number.isFinite(a)) return '';
    let base = `${h}-${a}`;
    if (h === a && pred.clasificado_codigo) {
        const equipo = pred.clasificado_codigo === partido.equipo_local_codigo
            ? (partido.equipo_local_nombre || partido.equipo_local_codigo)
            : (partido.equipo_visitante_nombre || partido.equipo_visitante_codigo);
        base += ` (pasa ${equipo})`;
    }
    return base;
};

// Fallback de banderas por código FIFA — usado cuando ni `bandera_url`
// (vendrá cuando DT-DN se cierre con JOIN equipos↔partidos) ni un emoji
// explícito están disponibles. Cubre los 48 clasificables del Mundial 2026.
const FLAG_EMOJI_DEFAULT = {
    ARG: '🇦🇷', BRA: '🇧🇷', URU: '🇺🇾', CHI: '🇨🇱', COL: '🇨🇴', PER: '🇵🇪',
    ECU: '🇪🇨', BOL: '🇧🇴', PAR: '🇵🇾', VEN: '🇻🇪', MEX: '🇲🇽', USA: '🇺🇸',
    CAN: '🇨🇦', CRC: '🇨🇷', PAN: '🇵🇦', HON: '🇭🇳', JAM: '🇯🇲',
    ESP: '🇪🇸', FRA: '🇫🇷', GER: '🇩🇪', ITA: '🇮🇹', POR: '🇵🇹', NED: '🇳🇱',
    BEL: '🇧🇪', SUI: '🇨🇭', DEN: '🇩🇰', CRO: '🇭🇷', POL: '🇵🇱', SRB: '🇷🇸',
    AUT: '🇦🇹', UKR: '🇺🇦', NOR: '🇳🇴', SWE: '🇸🇪', ENG: '🏴', WAL: '🏴',
    SCO: '🏴', IRL: '🇮🇪',
    JPN: '🇯🇵', KOR: '🇰🇷', AUS: '🇦🇺', KSA: '🇸🇦', QAT: '🇶🇦', IRN: '🇮🇷',
    SEN: '🇸🇳', MAR: '🇲🇦', CMR: '🇨🇲', GHA: '🇬🇭', TUN: '🇹🇳', EGY: '🇪🇬',
    NGA: '🇳🇬', RSA: '🇿🇦',
};

const bandera = (urlOrEmoji, code) => {
    if (urlOrEmoji && typeof urlOrEmoji === 'string') {
        if (urlOrEmoji.startsWith('http')) return `<img class="ppc-flag-img" src="${urlOrEmoji}" alt="${code || ''}">`;
        return `<span class="ppc-flag-emoji">${urlOrEmoji}</span>`;
    }
    const emoji = FLAG_EMOJI_DEFAULT[code] || '⚽';
    return `<span class="ppc-flag-emoji">${emoji}</span>`;
};

// ─── CSS exportado (consumer lo inyecta donde toque) ───────────────────────
// Selectores prefijados .ppc-* para no colisionar. Variables CSS (--celeste,
// --white, --text-muted, --font-heading) son las globales del producto;
// existen también dentro del Shadow DOM de Prode porque CSS variables
// pasan a través del shadow boundary.

export const PLAYOFF_CARD_CSS = `
    /* Quita spinners en inputs number */
    .ppc-wrap input[type="number"]::-webkit-inner-spin-button,
    .ppc-wrap input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
    .ppc-wrap input[type="number"] { -moz-appearance: textfield; appearance: textfield; }

    /* ─── Formato HOME (banderas grandes + inputs inline) ────────────── */
    .ppc-home { position: relative; padding: 1.6rem 1.2rem 1.2rem; border: 1px solid rgba(255,255,255,0.18); background: rgba(255,255,255,0.03); border-radius: 12px; display: flex; flex-direction: column; }
    .ppc-home-statusbox { position: absolute; top: 0.6rem; right: 0.7rem; display: flex; flex-direction: column; align-items: flex-end; gap: 0.15rem; z-index: 1; }
    .ppc-home-badge { font-size: 0.6rem; font-weight: 800; letter-spacing: 0.5px; padding: 0.18rem 0.45rem; border-radius: 3px; text-transform: uppercase; background: rgba(117,170,219,0.18); color: var(--celeste); border: 1px solid rgba(117,170,219,0.3); }
    .ppc-home-badge.live { background: rgba(255,107,107,0.18); color: #ff6b6b; border-color: rgba(255,107,107,0.4); animation: ppcPulse 1.5s infinite; }
    .ppc-home-badge.finished { background: rgba(255,255,255,0.05); color: var(--text-muted); border-color: rgba(255,255,255,0.1); }
    .ppc-home-substatus { text-align: right; font-size: 0.6rem; font-weight: 700; color: var(--text-muted); text-transform: none; }
    @keyframes ppcPulse { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
    .ppc-home-ctx { text-align: center; font-size: 0.7rem; color: var(--text-muted); letter-spacing: 1px; font-weight: 700; margin-bottom: 0.8rem; margin-top: 0.4rem; }
    .ppc-home-teams { display: grid; grid-template-columns: auto auto auto auto auto; align-items: center; column-gap: 0.5rem; justify-content: center; margin: 0.4rem 0 0.9rem; }
    /* Marcador REAL (amarillo, SIN caja) vs inputs de edición: se togglean con .editing en el wrap. */
    /* Marcador REAL: SIEMPRE visible (amarillo, sin caja). La edición vive aparte abajo. */
    .ppc-home-score { display: inline-flex; align-items: center; justify-content: center; min-width: 56px; }
    .ppc-home-marcador { color: var(--gold, #f6c453); font-family: var(--font-heading); font-size: 1.5rem; font-weight: 900; white-space: nowrap; letter-spacing: .01em; }
    .ppc-home-teams .ppc-code { font-family: var(--font-heading); color: var(--white); font-size: 1rem; }
    .ppc-home-teams .ppc-flag-emoji { font-size: 1.8rem; line-height: 1; }
    /* Bandera real (img) en HOME — dimensionada (espeja la .flag de grupos, un toque
       más grande para el formato "banderas grandes"). Sin esto el PNG renderiza a
       tamaño natural y descuadra el grid. */
    .ppc-home-teams .ppc-flag-img { width: 32px; height: 22px; object-fit: cover; border-radius: 5px; border: 1px solid rgba(255,255,255,.12); background: rgba(255,255,255,.07); }
    /* Inputs de goles del HOME — ahora viven en el bloque de edición .ppc-edit (abajo). */
    .ppc-edit .ppc-input { width: 50px; padding: 0.45rem 0.2rem; font-size: 1.35rem; text-align: center; border: 1px solid rgba(255,255,255,0.2); border-radius: 8px; background: rgba(255,255,255,0.05); color: var(--white); font-weight: 900; font-family: var(--font-heading); }
    .ppc-edit .ppc-input:focus { outline: 2px solid var(--celeste); border-color: var(--celeste); }
    .ppc-edit .ppc-input:disabled { opacity: 0.55; cursor: not-allowed; }
    .ppc-edit .ppc-input::placeholder { color: var(--gold, #f6c453); opacity: 1; font-weight: 900; }
    .ppc-edit .ppc-sep { color: var(--text-muted); font-size: 1.4rem; font-weight: 900; }
    .ppc-home-save { width: 100%; margin-top: 1rem; padding: 0.75rem; font-size: 0.9rem; font-weight: 800; background: linear-gradient(135deg, #3ddc84, #2fb872); color: #06111f; border: none; border-radius: 8px; cursor: pointer; letter-spacing: 0.3px; }
    .ppc-home-save:disabled { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.4); cursor: not-allowed; }
    .ppc-home-save.editing { background: transparent; color: #bbf7d0; border: 1px solid rgba(61,220,132,.28); font-weight: 900; padding: 0.55rem; }
    .ppc-home-saved-line { display: none; margin-top: 0.7rem; padding: 0.5rem 0.7rem; border-radius: 8px; background: rgba(117,170,219,.07); border: 1px solid rgba(117,170,219,.18); color: rgba(226,232,240,.94); font-size: 0.85rem; font-weight: 700; text-align: center; }
    .ppc-home-saved-line.visible { display: block; }
    .ppc-home-saved-line strong { color: #bbf7d0; font-weight: 900; }

    /* Línea "Clasifica: XXX (por penales)" en cards playoff finalizadas con
       empate resuelto por penales. Color verde sutil para diferenciar del
       resultado y de la línea "Jugaste:". Usa código de 3 letras (cabe siempre
       en mobile, sin riesgo de overflow con nombres largos tipo "Bosnia-Herzegovina"). */
    .ppc-prode-clasif-real { margin: 6px auto 0; color: rgba(187,247,208,.78); font-size: 11px; font-weight: 800; text-align: center; }
    .ppc-prode-clasif-real strong { color: #bbf7d0; font-weight: 900; }

    /* ─── Formato PRODE (fila compacta) ──────────────────────────────── */
    .ppc-prode { border: 1px solid rgba(255,255,255,.105); border-radius: 11px; background: linear-gradient(135deg, rgba(255,255,255,.06), rgba(255,255,255,.025)); overflow: hidden; }
    .ppc-prode-head { padding: 9px 10px 9px; }
    .ppc-prode-topline { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; margin-bottom: 7px; }
    .ppc-prode-meta { min-width: 0; color: rgba(148,163,184,.8); font-size: 9.5px; font-weight: 900; letter-spacing: .04em; text-transform: uppercase; white-space: nowrap; }
    .ppc-prode-meta strong { color: rgba(248,250,252,.78); font-weight: 900; }
    .ppc-prode-status { flex: 0 0 auto; color: rgba(248,250,252,.62); font-size: 9.5px; font-weight: 900; letter-spacing: .04em; text-transform: uppercase; }
    .ppc-prode-status.live { color: #ff6b6b; }
    .ppc-prode-substatus { display: block; margin-top: 2px; font-size: 8.5px; font-weight: 800; letter-spacing: .03em; text-transform: uppercase; color: rgba(248,250,252,.62); }
    .ppc-prode-teams { display: grid; grid-template-columns: minmax(60px,1fr) 28px auto 28px minmax(60px,1fr); align-items: center; column-gap: 5px; width: 100%; margin: 0 auto; }
    /* Marcador REAL (amarillo, SIN caja) vs inputs de edición: togglean con .editing en el wrap. */
    /* Marcador REAL: SIEMPRE visible (amarillo, sin caja). La edición vive aparte abajo. */
    .ppc-prode-score { display: inline-flex; align-items: center; justify-content: center; min-width: 46px; }
    .ppc-prode-marcador { color: var(--gold, #f6c453); font-family: var(--font-heading); font-size: 17px; font-weight: 900; white-space: nowrap; letter-spacing: .01em; }
    .ppc-prode-name { min-width: 0; color: var(--white); font-size: 13px; font-weight: 900; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .ppc-prode-name.home { text-align: right; }
    .ppc-prode-name.away { text-align: left; }
    .ppc-prode-flag { display: inline-flex; align-items: center; justify-content: center; font-size: 22px; line-height: 1; }
    /* Bandera real (img) en PRODE — el helper renombra ppc-flag-img -> ppc-prode-flag,
       así que la misma clase la lleva el emoji-span y el <img>. Este selector más
       específico (img.) dimensiona SOLO la imagen (espeja la .flag de grupos, entra
       en la columna de 28px del grid) sin afectar el fallback emoji. */
    img.ppc-prode-flag { width: 28px; height: 20px; object-fit: cover; border-radius: 5px; border: 1px solid rgba(255,255,255,.12); background: rgba(255,255,255,.07); }
    .ppc-prode-input { width: 38px; padding: 5px 2px; font-size: 17px; text-align: center; border: 1px solid rgba(255,255,255,.18); border-radius: 7px; background: rgba(255,255,255,.05); color: var(--white); font-weight: 900; font-family: var(--font-heading); }
    .ppc-prode-input:focus { outline: 2px solid var(--celeste); border-color: var(--celeste); }
    .ppc-prode-input:disabled { opacity: 0.55; cursor: not-allowed; }
    .ppc-prode-input::placeholder { color: var(--gold); opacity: 1; font-weight: 900; }
    .ppc-prode-goal-sep { text-align: center; color: rgba(148,163,184,.78); font-size: 14px; font-weight: 900; }
    .ppc-prode-pred-line { display: none; margin: 6px auto 0; color: rgba(148,163,184,.9); font-size: 11px; font-weight: 800; text-align: center; }
    .ppc-prode-pred-line.visible { display: block; }
    .ppc-prode-pred-line strong { color: rgba(248,250,252,.92); font-weight: 900; }
    .ppc-prode-foot { display: grid; grid-template-columns: minmax(0,1fr) auto minmax(0,1fr); align-items: center; gap: 7px; margin-top: 7px; min-height: 26px; }
    .ppc-prode-cta { justify-self: end; min-height: 26px; padding: 0 12px; border: 1px solid rgba(61,220,132,.22); border-radius: 999px; color: #bbf7d0; background: rgba(61,220,132,.09); font-size: 10px; font-weight: 900; cursor: pointer; letter-spacing: 0.4px; }
    .ppc-prode-cta:hover { background: rgba(61,220,132,.15); border-color: rgba(61,220,132,.34); }
    .ppc-prode-cta:disabled { opacity: 0.45; cursor: not-allowed; }

    /* ─── Compartido: bloque de EDICIÓN (goles + penales) + error ─────── */
    /* El espacio de edición vive ABAJO, oculto en reposo; se abre con .editing en el wrap
       (al tocar Jugar/Editar). NUNCA toca el casillero del marcador real (que queda arriba). */
    .ppc-edit { display: none; margin-top: 0.7rem; padding-top: 0.6rem; border-top: 1px solid rgba(255,255,255,0.08); }
    .ppc-wrap.editing .ppc-edit { display: block; }
    .ppc-edit-title { display: block; text-align: center; color: var(--text-muted); font-size: 0.7rem; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; margin-bottom: 0.5rem; }
    .ppc-edit-goles { display: flex; align-items: center; justify-content: center; gap: 0.5rem; }
    .ppc-edit-code { font-family: var(--font-heading); color: var(--white); font-size: 0.95rem; font-weight: 900; }
    .ppc-pen { display: none; margin-top: 0.7rem; padding-top: 0.6rem; border-top: 1px solid rgba(255,255,255,0.08); }
    .ppc-pen.visible { display: block; }
    .ppc-pen-title { display: block; color: var(--white); font-size: 0.85rem; font-weight: 700; margin-bottom: 0.45rem; text-align: center; }
    .ppc-pen-row { display: flex; gap: 0.5rem; justify-content: center; }
    .ppc-pen-chip { padding: 0.45rem 0.9rem; font-size: 0.8rem; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.18); border-radius: 999px; color: var(--white); cursor: pointer; }
    .ppc-pen-chip.selected { background: var(--celeste); border-color: var(--celeste); color: #000; font-weight: 800; }
    .ppc-pen-chip:disabled { opacity: 0.55; cursor: not-allowed; }
    .ppc-error { display: none; margin-top: 0.6rem; padding: 0.45rem; border-radius: 6px; background: rgba(255,107,107,0.1); border: 1px solid rgba(255,107,107,0.3); color: #ff6b6b; font-size: 0.78rem; text-align: center; }
    .ppc-error.visible { display: block; }
`;

// ─── Helpers de meta visual ─────────────────────────────────────────────

const FASE_LABELS_FULL = { octavos: 'Octavos', cuartos: 'Cuartos', semifinal: 'Semifinal', final: 'Final' };
const FASE_LABELS_UPPER = { octavos: 'OCTAVOS', cuartos: 'CUARTOS', semifinal: 'SEMIFINAL', final: 'FINAL' };
const MES_ABREV = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];

const ART_FECHA_FMT = new Intl.DateTimeFormat('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit' });
const ART_HORA_FMT = new Intl.DateTimeFormat('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit', hour12: false });

const fmtCtxHome = (partido) => {
    const fase = FASE_LABELS_UPPER[partido.fase] || (partido.fase || '').toUpperCase();
    const parts = ART_FECHA_FMT.formatToParts(new Date(partido.fecha_hora));
    const d = parts.find(p => p.type === 'day')?.value || '';
    const m = parts.find(p => p.type === 'month')?.value || '';
    const dia = `${d} ${MES_ABREV[Number(m) - 1] || ''}`;
    const hora = ART_HORA_FMT.format(new Date(partido.fecha_hora));
    return `${fase} · ${dia} · ${hora}`;
};

const fmtCtxProde = (partido) => {
    const fase = FASE_LABELS_FULL[partido.fase] || partido.fase;
    const parts = ART_FECHA_FMT.formatToParts(new Date(partido.fecha_hora));
    const d = parts.find(p => p.type === 'day')?.value || '';
    const m = parts.find(p => p.type === 'month')?.value || '';
    const dia = `${d} ${MES_ABREV[Number(m) - 1] || ''}`;
    const hora = ART_HORA_FMT.format(new Date(partido.fecha_hora));
    return `${fase} &middot; ${dia} &middot; <strong>${hora}</strong>`;
};

const statusLabel = (status) => {
    if (status === 'live') return 'EN VIVO';
    if (status === 'finished') return 'FINALIZADO';
    return 'PROGRAMADO';
};

// ─── "Hasta X pts" + grilla "Ver puntos en juego" ─────────────────────────
// Espejo de prode.js getPuntosEnJuego/getResumenScoring para grupos. Acá la
// fórmula sigue el doc Prode_Scoring v1.2 §12-§14:
//   máximo = 80 × multiplicador_fase × (3 si ARG juega, 1 si no)
// Multiplicador: 16avos x1, octavos x2, cuartos x4, semis x8, final x12.
// Tercer puesto (partido.es_tercer_puesto=true) usa x1 (§13.1).
const PHASE_MULT = { '16avos': 1, octavos: 2, cuartos: 4, semifinal: 8, final: 12 };

const phaseMultiplier = (partido) => {
    if (partido?.es_tercer_puesto) return 1;
    return PHASE_MULT[partido?.fase] ?? 1;
};

const playsArgentina = (partido) =>
    partido?.equipo_local_codigo === 'ARG' || partido?.equipo_visitante_codigo === 'ARG';

export const getPuntosEnJuegoPlayoff = (partido) =>
    80 * phaseMultiplier(partido) * (playsArgentina(partido) ? 3 : 1);

export const getResumenScoringPlayoff = (partido) => {
    const mult = phaseMultiplier(partido);
    const lineas = [
        ['Acierto clasificado', '+10'],
        ['Acierto diferencia', '+10'],
        ['Acierto goles ganador', '+10'],
        ['Bonus acierto completo', '+50'],
        ['Multiplicador fase', `×${mult}`],
    ];
    if (playsArgentina(partido)) {
        lineas.push(['Bonus Argentina', '×3']);
    }
    return lineas;
};

const statusClass = (status) => {
    if (status === 'live') return 'live';
    if (status === 'finished') return 'finished';
    return '';
};

// ─── HTML — formato HOME ────────────────────────────────────────────────

export const renderPlayoffCardHome = (partido, prediccionInicial = null, isScoringOpen = false) => {
    const id = partido.partido_id;
    const status = partido.status || 'scheduled';
    const locked = status === 'live' || status === 'finished';
    const isFinished = status === 'finished';
    const enVivo = status === 'live';
    const estadoVivo = enVivo ? lineasEstadoVivo(partido) : null;
    // Marcador REAL (display AMARILLO, SIN caja), separado del input de predicción:
    // "– –" si no empezó, "X - Y" en vivo/finalizado. Igual que el .score de grupos.
    const marcadorReal = (partido.goles_local != null && partido.goles_visitante != null)
        ? `${partido.goles_local} - ${partido.goles_visitante}`
        : '– –';
    // No se puede predecir hasta que estén los DOS equipos (venga de derivación o de la
    // API). Igual que PRODE (puedePredecir). Si falta un lado → sin CTA Jugar/Editar.
    const predecible = partido.equipo_local_codigo != null && partido.equipo_visitante_codigo != null;
    // Desglose "Ver puntos en juego" — mismo scoring de playoff que la card de PRODE
    // (getResumenScoringPlayoff / getPuntosEnJuegoPlayoff: respeta multiplicador de
    // fase + Bonus Argentina). Reusa las clases home-* del <style> del home (light DOM,
    // grupo.js) y su listener delegado (.home-score-toggle -> openScoring + re-render).
    const maxPts = getPuntosEnJuegoPlayoff(partido);
    const scoringTabla = isScoringOpen
        ? `<div class="home-scoring-toggle"><div class="home-scoring-table">${getResumenScoringPlayoff(partido).map(([l, p]) => `<span class="home-scoring-label">${l}</span><span class="home-scoring-value">${p}</span>`).join('')}</div></div>`
        : '';
    const scoringBlock = `
                <div style="display:grid; grid-template-columns:1fr auto 1fr; align-items:center; margin-top:0.7rem;">
                    <span class="home-max-points">Hasta ${maxPts} pts</span>
                    <button class="home-score-toggle ${isScoringOpen ? 'active' : ''}" type="button" data-score="${id}">Ver puntos en juego</button>
                    <span></span>
                </div>
                ${scoringTabla}`;
    return `
        <div class="ppc-wrap" data-card="${partido.partido_id}">
            <div class="ppc-home" data-ppc-home="${id}">
                <div class="ppc-home-statusbox">
                    <span class="ppc-home-badge ${statusClass(status)}">${statusLabel(status)}</span>
                    ${estadoVivo ? `<span class="ppc-home-substatus">${estadoVivo.subestado}${estadoVivo.minuto ? ` · ${estadoVivo.minuto}` : ''}</span>` : ''}
                </div>
                <div class="ppc-home-ctx">${fmtCtxHome(partido)}</div>
                <div class="ppc-home-teams">
                    <span class="ppc-code">${partido.equipo_local_codigo || 'A definir'}</span>
                    ${bandera(partido.equipo_local_bandera_url || partido.equipo_local_bandera_emoji, partido.equipo_local_codigo)}
                    <span class="ppc-home-score">
                        <span class="ppc-home-marcador">${marcadorReal}</span>
                    </span>
                    ${bandera(partido.equipo_visitante_bandera_url || partido.equipo_visitante_bandera_emoji, partido.equipo_visitante_codigo)}
                    <span class="ppc-code">${partido.equipo_visitante_codigo || 'A definir'}</span>
                </div>
                <div class="ppc-edit" data-ppc-edit>
                    <span class="ppc-edit-title">Tu predicción — goles por equipo</span>
                    <div class="ppc-edit-goles">
                        <span class="ppc-edit-code">${partido.equipo_local_codigo}</span>
                        <input class="ppc-input" type="number" min="0" max="15" inputmode="numeric" placeholder="–" data-ppc-goals="home" ${locked ? 'disabled' : ''}>
                        <span class="ppc-sep">−</span>
                        <input class="ppc-input" type="number" min="0" max="15" inputmode="numeric" placeholder="–" data-ppc-goals="away" ${locked ? 'disabled' : ''}>
                        <span class="ppc-edit-code">${partido.equipo_visitante_codigo}</span>
                    </div>
                    <div class="ppc-pen" data-ppc-pen-block>
                        <strong class="ppc-pen-title">¿Quién pasa por penales?</strong>
                        <div class="ppc-pen-row">
                            <button class="ppc-pen-chip" type="button" data-ppc-pen="${partido.equipo_local_codigo}" ${locked ? 'disabled' : ''}>${partido.equipo_local_codigo}</button>
                            <button class="ppc-pen-chip" type="button" data-ppc-pen="${partido.equipo_visitante_codigo}" ${locked ? 'disabled' : ''}>${partido.equipo_visitante_codigo}</button>
                        </div>
                    </div>
                </div>
                ${scoringBlock}
                <div class="ppc-error" data-ppc-error></div>
                <div class="ppc-home-saved-line" data-ppc-saved></div>
                ${locked || !predecible ? '' : `<button class="ppc-home-save" type="button" data-ppc-save>${prediccionInicial ? 'Editar predicción' : 'Jugar'}</button>`}
            </div>
        </div>
    `;
};

// ─── HTML — formato PRODE ──────────────────────────────────────────────

// Helpers inline para el chip "+N pts" / "-N pts" en finished. Reusan las
// clases .result-points / .result-points.positive / .result-points.negative
// que viven en PRODE_CSS (disponibles tanto en el shadow del Prode como en
// el shadow del perfil, donde mountMatchCardShadow inyecta PRODE_CSS).
const puntosClassPlayoff = (p) => p > 0 ? 'positive' : (p < 0 ? 'negative' : 'neutral');
const puntosLabelPlayoff = (p) => (p > 0 ? `+${p}` : `${p}`) + ' pts';

export const renderPlayoffCardProde = (partido, prediccionInicial = null, isScoringOpen = false) => {
    const id = partido.partido_id;
    const status = partido.status || 'scheduled';
    const locked = status === 'live' || status === 'finished';
    const maxPts = getPuntosEnJuegoPlayoff(partido);
    const resumen = getResumenScoringPlayoff(partido);
    const puntos = prediccionInicial?.puntos ?? 0;
    const chipFinished = `<span class="result-points ${puntosClassPlayoff(puntos)}">${puntosLabelPlayoff(puntos)}</span>`;
    // Línea "Clasifica: XXX (por penales)" solo en finished + empate + clasificado real
    // definido en el PARTIDO (no en la predicción). Si pinta el resultado real, no
    // lo que predijo el jugador. Usa código FIFA 3 letras (cabe en mobile).
    const muestraClasifReal = status === 'finished'
        && partido.goles_local != null
        && partido.goles_visitante != null
        && partido.goles_local === partido.goles_visitante
        && partido.ganador_penales_codigo;
    const clasifRealHTML = muestraClasifReal
        ? `<div class="ppc-prode-clasif-real">Clasifica: <strong>${partido.ganador_penales_codigo}</strong> (por penales)</div>`
        : '';
    const isFinished = status === 'finished';
    const enVivo = status === 'live';
    const estadoVivo = enVivo ? lineasEstadoVivo(partido) : null;
    // Marcador REAL del partido (display AMARILLO, SIN caja), separado del input de
    // predicción: "– –" si no empezó, "X - Y" en vivo/finalizado. Igual que el .score
    // de la card de grupos. Los inputs viven aparte y solo se ven en modo edición.
    const marcadorReal = (partido.goles_local != null && partido.goles_visitante != null)
        ? `${partido.goles_local} - ${partido.goles_visitante}`
        : '– –';
    // "Jugaste: X-Y" (la PREDICCIÓN del jugador) — SEPARADA del marcador real. Visible
    // siempre que haya predicción (cualquier estado). "No jugaste" solo en finished sin
    // pred. (En perfil-prode NO se llama wirePlayoffCard, por eso se pinta del HTML.)
    const noJugasteFinished = isFinished && !prediccionInicial;
    let jugasteHTML = '';
    if (prediccionInicial) {
        const sh = prediccionInicial.goalsHome;
        const sa = prediccionInicial.goalsAway;
        let label = `${sh}-${sa}`;
        if (sh === sa && prediccionInicial.clasificado_codigo) label += ` (pasa ${prediccionInicial.clasificado_codigo})`;
        jugasteHTML = `Jugaste: <strong>${label}</strong>`;
    }
    const predLineContent = noJugasteFinished ? 'No jugaste este partido' : jugasteHTML;
    const predLineVisible = noJugasteFinished || !!prediccionInicial;
    return `
        <div class="ppc-wrap" data-card="${partido.partido_id}">
            <div class="ppc-prode" data-ppc-prode="${id}">
                <div class="ppc-prode-head">
                    <div class="ppc-prode-topline">
                        <div class="ppc-prode-meta">${fmtCtxProde(partido)}</div>
                        <div class="ppc-prode-status ${statusClass(status)}"><span>${statusLabel(status)}</span>${estadoVivo ? `<span class="ppc-prode-substatus">${estadoVivo.subestado}${estadoVivo.minuto ? ` · ${estadoVivo.minuto}` : ''}</span>` : ''}</div>
                    </div>
                    <div class="ppc-prode-teams">
                        <span class="ppc-prode-name home">${partido.equipo_local_nombre || partido.equipo_local_codigo}</span>
                        ${bandera(partido.equipo_local_bandera_url || partido.equipo_local_bandera_emoji, partido.equipo_local_codigo).replace('ppc-flag-emoji','ppc-prode-flag').replace('ppc-flag-img','ppc-prode-flag')}
                        <span class="ppc-prode-score">
                            <span class="ppc-prode-marcador">${marcadorReal}</span>
                        </span>
                        ${bandera(partido.equipo_visitante_bandera_url || partido.equipo_visitante_bandera_emoji, partido.equipo_visitante_codigo).replace('ppc-flag-emoji','ppc-prode-flag').replace('ppc-flag-img','ppc-prode-flag')}
                        <span class="ppc-prode-name away">${partido.equipo_visitante_nombre || partido.equipo_visitante_codigo}</span>
                    </div>
                    ${clasifRealHTML}
                    <div class="ppc-prode-pred-line${predLineVisible ? ' visible' : ''}" data-ppc-pred>${predLineContent}</div>
                    <div class="ppc-edit" data-ppc-edit>
                        <span class="ppc-edit-title">Tu predicción — goles por equipo</span>
                        <div class="ppc-edit-goles">
                            <span class="ppc-edit-code">${partido.equipo_local_codigo}</span>
                            <input class="ppc-prode-input" type="number" min="0" max="15" inputmode="numeric" placeholder="–" data-ppc-goals="home" ${locked ? 'disabled' : ''}>
                            <span class="ppc-prode-goal-sep">−</span>
                            <input class="ppc-prode-input" type="number" min="0" max="15" inputmode="numeric" placeholder="–" data-ppc-goals="away" ${locked ? 'disabled' : ''}>
                            <span class="ppc-edit-code">${partido.equipo_visitante_codigo}</span>
                        </div>
                        <div class="ppc-pen" data-ppc-pen-block>
                            <strong class="ppc-pen-title">¿Quién pasa por penales?</strong>
                            <div class="ppc-pen-row">
                                <button class="ppc-pen-chip" type="button" data-ppc-pen="${partido.equipo_local_codigo}" ${locked ? 'disabled' : ''}>${partido.equipo_local_nombre || partido.equipo_local_codigo}</button>
                                <button class="ppc-pen-chip" type="button" data-ppc-pen="${partido.equipo_visitante_codigo}" ${locked ? 'disabled' : ''}>${partido.equipo_visitante_nombre || partido.equipo_visitante_codigo}</button>
                            </div>
                        </div>
                    </div>
                    <div class="ppc-prode-foot">
                        <span class="max-points">Hasta ${maxPts} pts</span>
                        <button class="score-toggle-inline${isScoringOpen ? ' active' : ''}" type="button" data-score="${id}">Ver puntos en juego</button>
                        ${status === 'finished'
                            ? chipFinished
                            : locked
                                ? '<span></span>'
                                : `<button class="ppc-prode-cta" type="button" data-ppc-save data-cta-label="${prediccionInicial ? 'EDITAR' : 'JUGAR'}">${prediccionInicial ? 'EDITAR' : 'JUGAR'}</button>`}
                    </div>
                </div>
                <div class="scoring-toggle-wrapper" data-scoring-wrapper="${id}"${isScoringOpen ? '' : ' style="display:none;"'}>
                    <div class="scoring-toggle">
                        <div class="scoring-table">
                            ${resumen.map(([l, p]) => `<span class="scoring-label">${l}</span><span class="scoring-value">${p}</span>`).join('')}
                        </div>
                    </div>
                </div>
                <div class="ppc-error" data-ppc-error></div>
            </div>
        </div>
    `;
};

// ─── Wiring (compartido entre los dos formatos) ─────────────────────────
// El root es el contenedor del .ppc-wrap. callbacks.onSave(payload) debe
// devolver una promesa que resuelva con {ok: true, data} u {ok: false, error}.
// El componente colapsa a "saved" solo si onSave resuelve OK.

export const wirePlayoffCard = (rootElement, partido, prediccionInicial = null, callbacks = {}) => {
    if (!rootElement) return;
    const isHome = !!rootElement.querySelector('[data-ppc-home]');
    const inputs = rootElement.querySelectorAll('input[data-ppc-goals]');
    const penBlock = rootElement.querySelector('[data-ppc-pen-block]');
    const penChips = rootElement.querySelectorAll('[data-ppc-pen]');
    const errorEl = rootElement.querySelector('[data-ppc-error]');
    const saveBtn = rootElement.querySelector('[data-ppc-save]');
    const savedLine = rootElement.querySelector('[data-ppc-saved]');     // solo HOME
    const predLine = rootElement.querySelector('[data-ppc-pred]');        // solo PRODE
    const status = partido.status || 'scheduled';
    const locked = status === 'live' || status === 'finished';

    const isFinished = status === 'finished';
    // El .ppc-wrap lleva la clase `.editing` que togglea (vía CSS) marcador real <-> inputs.
    const wrap = rootElement.classList?.contains('ppc-wrap')
        ? rootElement
        : (rootElement.querySelector?.('.ppc-wrap') || rootElement);

    const state = {
        goalsHome: prediccionInicial?.goalsHome ?? '',
        goalsAway: prediccionInicial?.goalsAway ?? '',
        clasificado_codigo: prediccionInicial?.clasificado_codigo ?? null,
        saved: prediccionInicial != null,
        editing: false,
        sending: false,
    };

    // Los casilleros NO se siembran con la predicción en la vista inicial: muestran el
    // PLACEHOLDER "–" (scheduled) o el RESULTADO REAL (live/finished, vía value= del HTML
    // desde render*), igual que la card de grupos. La predicción se ve aparte en
    // "Jugaste: X-Y". Recién al entrar a EDITAR (botón Editar/JUGAR) sembramos los inputs
    // para poder ajustarla.
    const seedInputs = () => {
        inputs.forEach((el) => {
            const which = el.dataset.ppcGoals;
            if (which === 'home' && state.goalsHome !== '') el.value = state.goalsHome;
            if (which === 'away' && state.goalsAway !== '') el.value = state.goalsAway;
        });
    };

    const refresh = () => {
        const ambos = state.goalsHome !== '' && state.goalsAway !== '';
        const isTie = ambos && Number(state.goalsHome) === Number(state.goalsAway);

        // Bloque penales: SOLO en modo EDICIÓN + empate (su propio espacio, separado).
        penBlock?.classList.toggle('visible', state.editing && isTie);
        if (!isTie) state.clasificado_codigo = null;
        penChips.forEach((c) => c.classList.toggle('selected', c.getAttribute('data-ppc-pen') === state.clasificado_codigo));

        // "Jugaste: X-Y" (la predicción) — SOLO en reposo (no editando), separada del
        // marcador real. "No jugaste este partido" solo en finished sin predicción.
        const jugasteEl = savedLine || predLine;
        if (jugasteEl) {
            if (state.saved && !state.editing) {
                jugasteEl.innerHTML = `Jugaste: <strong>${labelPrediccion(state, partido)}</strong>`;
                jugasteEl.classList.add('visible');
            } else if (isFinished && !state.saved) {
                jugasteEl.innerHTML = 'No jugaste este partido';
                jugasteEl.classList.add('visible');
            } else {
                jugasteEl.classList.remove('visible');
                jugasteEl.innerHTML = '';
            }
        }

        // CTA Jugar/Editar/Guardar (solo existe en no-locked). Abrir edición vs guardar
        // lo decide el handler de click según state.editing.
        if (saveBtn) {
            let label;
            if (state.sending) label = isHome ? 'Guardando…' : 'GUARDANDO…';
            else if (state.editing) label = isHome ? 'Guardar predicción' : 'GUARDAR';
            else if (state.saved) label = isHome ? 'Editar predicción' : 'EDITAR';
            else label = isHome ? 'Jugar' : 'JUGAR';
            saveBtn.textContent = label;
            if (!isHome) saveBtn.setAttribute('data-cta-label', label);
            // El look "Editar predicción" (verde outline) solo en reposo con predicción.
            saveBtn.classList.toggle('editing', isHome && state.saved && !state.editing);
            saveBtn.disabled = state.sending;
        }

        if (errorEl && !state.sending) errorEl.classList.remove('visible');
    };

    inputs.forEach((el) => {
        el.addEventListener('input', (e) => {
            if (el.dataset.ppcGoals === 'home') state.goalsHome = e.target.value;
            if (el.dataset.ppcGoals === 'away') state.goalsAway = e.target.value;
            refresh();
        });
    });

    penChips.forEach((chip) => {
        chip.addEventListener('click', () => {
            if (chip.disabled || state.sending) return;
            state.clasificado_codigo = chip.getAttribute('data-ppc-pen');
            refresh();
        });
    });

    saveBtn?.addEventListener('click', async () => {
        if (state.sending) return;
        // No estamos editando → el botón ABRE el espacio de edición (Jugar/Editar):
        // muestra los inputs (clase .editing en el wrap), siembra la predicción guardada
        // para ajustarla y enfoca el primer casillero. NO valida ni guarda todavía.
        if (!state.editing) {
            state.editing = true;
            wrap?.classList.add('editing');
            seedInputs();
            refresh();
            rootElement.querySelector('input[data-ppc-goals]')?.focus?.();
            return;
        }
        // En modo edición → GUARDAR.
        if (!isPlayoffPredictionReady(state)) {
            if (errorEl) {
                const ambos = state.goalsHome !== '' && state.goalsAway !== '';
                errorEl.textContent = ambos
                    ? 'Empate sin clasificado: elegí quién pasa por penales.'
                    : 'Cargá los goles de ambos equipos.';
                errorEl.classList.add('visible');
            }
            return;
        }
        const payload = {
            partido_id: partido.partido_id,
            prediction_type: 'playoff_match',
            score_local: Number(state.goalsHome),
            score_visitante: Number(state.goalsAway),
            clasificado_codigo: state.clasificado_codigo,
        };
        state.sending = true;
        refresh();
        try {
            const result = await callbacks.onSave?.(payload);
            if (result && result.ok === false) {
                if (errorEl) {
                    errorEl.textContent = result.message || 'No pudimos guardar la predicción. Probá de nuevo.';
                    errorEl.classList.add('visible');
                }
                state.saved = false;
            } else {
                state.saved = true;
                state.editing = false;
                wrap?.classList.remove('editing');   // volver a reposo: marcador real + "Jugaste"
            }
        } catch (err) {
            if (errorEl) {
                errorEl.textContent = 'No pudimos guardar la predicción. Probá de nuevo.';
                errorEl.classList.add('visible');
            }
            state.saved = false;
        } finally {
            state.sending = false;
            refresh();
        }
    });

    refresh();
};
